// Administrative boundaries on the main map: a soft "flash" of the region
// containing a fresh event, and a static reference overlay of
// country/province/district lines.
//
// Flash taste rules (annoyance is the failure mode):
//   - The envelope (fade in, hold, fade out) repeats `pulses` times per event
//     — shipped default ONE, so it never blinks unless an operator asks.
//   - An area already lit stays lit (its clear-timer extends) — no restarts.
//   - At most `maxActive` areas lit at once; extras are dropped, not queued.
//   - Suppressed while the Studio is open and by the Layers toggle.
//
// Everything tunable here is the admin setting `regionFlash` (its own console
// tab): which kinds trigger it lives with the trigger in panels.flashUpdates,
// the envelope/level/intensity live here. One config, read via
// regionFlashCfg() so trigger and renderer can never disagree.

import type { Feature, Geometry } from 'geojson';
import * as maplibregl from 'maplibre-gl';
import { get } from './api';

interface ContainingDto {
  fid: number;
  level: number;
  group: string;
  name: string | null;
  geometry: Geometry;
}

export interface RegionFlashConfig {
  enabled: boolean;
  kinds: string[];
  level: 0 | 1;          // 1 = province, 0 = country
  pulses: number;        // envelope repeats per event
  seconds: number;       // length of ONE envelope
  maxAgeMinutes: number; // only events younger than this flash
  maxActive: number;
  fillOpacity: number;
  lineOpacity: number;
}

// MUST mirror the server's DEFAULT_REGION_FLASH (settings/service.py) — the
// behaviour before the setting existed, so a deployment that never opens the
// tab renders identically.
export const REGION_FLASH_DEFAULTS: RegionFlashConfig = {
  enabled: true,
  kinds: ['launch', 'impact', 'airstrike', 'drone'],
  level: 1,
  pulses: 1,
  seconds: 3,
  maxAgeMinutes: 10,
  maxActive: 3,
  fillOpacity: 0.16,
  lineOpacity: 0.85,
};

const FLASH_SRC = 'flash-areas';
// The pulse envelope: fade in, hold fully lit, fade out. Calibrated to a ~3 s
// total and scaled together by regionFlash.seconds.
const BASE_IN_MS = 250, BASE_HOLD_MS = 550, BASE_FADE_MS = 2200;
let IN_MS = BASE_IN_MS;      // fade in
let HOLD_MS = BASE_HOLD_MS;  // fully lit
let FADE_MS = BASE_FADE_MS;  // fade out

let cfg: RegionFlashConfig = REGION_FLASH_DEFAULTS;
let kindSet = new Set(cfg.kinds);

const num = (v: unknown, fallback: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? v : fallback;

/** Apply the admin `regionFlash` setting (partial/unknown-shaped input is
 *  fine — the server validated it; anything missing keeps the default). */
export function setRegionFlash(raw?: unknown): void {
  const r = (raw ?? {}) as Partial<Record<keyof RegionFlashConfig, unknown>>;
  const d = REGION_FLASH_DEFAULTS;
  cfg = {
    enabled: typeof r.enabled === 'boolean' ? r.enabled : d.enabled,
    kinds: Array.isArray(r.kinds) ? r.kinds.map(String) : d.kinds,
    level: r.level === 0 ? 0 : 1,
    pulses: Math.max(1, Math.round(num(r.pulses, d.pulses))),
    seconds: num(r.seconds, d.seconds),
    maxAgeMinutes: num(r.maxAgeMinutes, d.maxAgeMinutes),
    maxActive: Math.max(1, Math.round(num(r.maxActive, d.maxActive))),
    fillOpacity: num(r.fillOpacity, d.fillOpacity),
    lineOpacity: num(r.lineOpacity, d.lineOpacity),
  };
  kindSet = new Set(cfg.kinds);
  const factor = Math.max(0.1, cfg.seconds) * 1000
    / (BASE_IN_MS + BASE_HOLD_MS + BASE_FADE_MS);
  IN_MS = Math.round(BASE_IN_MS * factor);
  HOLD_MS = Math.round(BASE_HOLD_MS * factor);
  FADE_MS = Math.round(BASE_FADE_MS * factor);
}

/** The live config plus derived forms the trigger site needs. */
export function regionFlashCfg(): RegionFlashConfig & {
  kindSet: ReadonlySet<string>; maxAgeMs: number;
} {
  return { ...cfg, kindSet, maxAgeMs: cfg.maxAgeMinutes * 60_000 };
}

// Keyed by `level:fid` — fids are only unique within one admin level, and the
// level is now a setting that can change mid-session.
const active = new Map<string, { feature: Feature; clearAt: number }>();
const lookupCache = new Map<string, ContainingDto | null>();
let fadeTimer: number | null = null;
let clearTimer: number | null = null;

function beforeLayer(map: maplibregl.Map): string | undefined {
  // Boundaries sit under the objects so pins/polygons stay crisp.
  return map.getLayer('entities-fill') ? 'entities-fill' : undefined;
}

function ensureFlashLayers(map: maplibregl.Map): void {
  if (map.getSource(FLASH_SRC)) return;
  map.addSource(FLASH_SRC, {
    type: 'geojson', data: { type: 'FeatureCollection', features: [] },
  });
  map.addLayer({
    id: 'flash-fill', type: 'fill', source: FLASH_SRC,
    paint: { 'fill-color': ['get', 'color'] as never, 'fill-opacity': 0 },
  }, beforeLayer(map));
  map.addLayer({
    id: 'flash-line', type: 'line', source: FLASH_SRC,
    paint: { 'line-color': ['get', 'color'] as never, 'line-width': 1.8, 'line-opacity': 0 },
  }, beforeLayer(map));
}

// Split a MultiPolygon into one Feature per polygon. MapLibre segments a fill
// bucket per feature-polygon and can't spill a single one past its 65535-vertex
// cap, so a dense province is safe once its parts are separate features rather
// than one aggregate. Single Polygons (and anything else) pass through as-is.
function explode(feature: Feature): Feature[] {
  const g = feature.geometry;
  if (g.type !== 'MultiPolygon') return [feature];
  return g.coordinates.map((coordinates) => ({
    ...feature,
    geometry: { type: 'Polygon', coordinates },
  }));
}

function syncFlashData(map: maplibregl.Map): void {
  (map.getSource(FLASH_SRC) as maplibregl.GeoJSONSource | undefined)?.setData({
    type: 'FeatureCollection',
    features: [...active.values()].flatMap((a) => explode(a.feature)),
  });
}

function setFlashOpacity(map: maplibregl.Map, fill: number, line: number, ms: number): void {
  if (!map.getLayer('flash-fill')) return;
  map.setPaintProperty('flash-fill', 'fill-opacity-transition', { duration: ms, delay: 0 });
  map.setPaintProperty('flash-line', 'line-opacity-transition', { duration: ms, delay: 0 });
  map.setPaintProperty('flash-fill', 'fill-opacity', fill);
  map.setPaintProperty('flash-line', 'line-opacity', line);
}

// One envelope cycle end-to-end, plus the settle gap before the next.
function cycleMs(): number { return IN_MS + HOLD_MS + FADE_MS + 50; }

// How many envelope repeats the CURRENT flash run still owes. Shared like the
// envelope itself: a new event resets it, so concurrent areas blink together
// rather than strobing on independent clocks.
let pulsesLeft = 0;

function pulse(map: maplibregl.Map): void {
  // (Re)run the shared pulse: quick in, slow out. Concurrent areas share the
  // envelope — calmer than independent strobes.
  if (fadeTimer !== null) clearTimeout(fadeTimer);
  if (clearTimer !== null) clearTimeout(clearTimer);
  setFlashOpacity(map, cfg.fillOpacity, cfg.lineOpacity, IN_MS);
  fadeTimer = window.setTimeout(() => {
    fadeTimer = null;
    setFlashOpacity(map, 0, 0, FADE_MS);
  }, IN_MS + HOLD_MS);
  clearTimer = window.setTimeout(() => {
    clearTimer = null;
    pulsesLeft -= 1;
    if (pulsesLeft > 0 && active.size) { pulse(map); return; }
    const now = Date.now();
    for (const [fid, a] of active) if (a.clearAt <= now) active.delete(fid);
    syncFlashData(map);
    if (active.size) {                 // late joiners keep their own pulse run
      pulsesLeft = cfg.pulses;
      pulse(map);
    }
  }, cycleMs());
}

/**
 * Flash the configured admin area (province by default) containing
 * [lon, lat]. Fire-and-forget; silently does nothing over open water, on
 * lookup failure, or when the budget is spent.
 */
export async function flashContaining(map: maplibregl.Map, lon: number, lat: number,
                                      color: string): Promise<void> {
  if (document.querySelector('.studio')) return;   // never flash under the light table
  const level = cfg.level;
  const key = `${level}:${lon.toFixed(1)},${lat.toFixed(1)}`;
  let area = lookupCache.get(key);
  if (area === undefined) {
    try {
      area = await get<ContainingDto>(
        `/api/boundaries/containing?lon=${lon}&lat=${lat}&level=${level}`);
    } catch {
      area = null;                                  // open water / no coverage
    }
    lookupCache.set(key, area);
  }
  if (!area) return;
  const runMs = cfg.pulses * cycleMs();
  const areaKey = `${level}:${area.fid}`;
  const existing = active.get(areaKey);
  if (existing) {
    existing.clearAt = Date.now() + runMs;
    return;                                         // already lit: extend, don't restart
  }
  if (active.size >= cfg.maxActive) return;
  ensureFlashLayers(map);
  active.set(areaKey, {
    feature: {
      type: 'Feature', geometry: area.geometry,
      properties: { color, name: area.name },
    },
    clearAt: Date.now() + runMs,
  });
  syncFlashData(map);
  pulsesLeft = cfg.pulses;
  pulse(map);
}

// --- static reference overlay ------------------------------------------------

const ADM_SRC = 'adm-boundaries';
const ADM_LAYERS = ['adm0-line', 'adm1-line', 'adm2-line'];

export function setAdmOverlay(map: maplibregl.Map, on: boolean): void {
  if (!map.getSource(ADM_SRC)) {
    if (!on) return;
    map.addSource(ADM_SRC, {
      type: 'vector',
      tiles: [`${location.origin}/api/tiles/adm/{z}/{x}/{y}`],
      minzoom: 0, maxzoom: 12,
    });
    const before = beforeLayer(map);
    map.addLayer({
      id: 'adm0-line', type: 'line', source: ADM_SRC, 'source-layer': 'adm0',
      paint: { 'line-color': '#4a5a70', 'line-width': 1.1, 'line-opacity': 0.55 },
    }, before);
    map.addLayer({
      id: 'adm1-line', type: 'line', source: ADM_SRC, 'source-layer': 'adm1',
      minzoom: 3,
      paint: { 'line-color': '#5b6b80', 'line-width': 0.8, 'line-opacity': 0.4,
               'line-dasharray': [4, 2] },
    }, before);
    map.addLayer({
      id: 'adm2-line', type: 'line', source: ADM_SRC, 'source-layer': 'adm2',
      minzoom: 8,
      paint: { 'line-color': '#5b6b80', 'line-width': 0.6, 'line-opacity': 0.3,
               'line-dasharray': [2, 2] },
    }, before);
  }
  for (const id of ADM_LAYERS) {
    if (map.getLayer(id)) {
      map.setLayoutProperty(id, 'visibility', on ? 'visible' : 'none');
    }
  }
}
